import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';

import { api } from '../api/endpoints';
import type { Permission, UserContext } from '../api/types';
import { meQueryKey } from '../app/queryClient';
import { useMe } from '../auth/context';
import { NotFoundPage } from '../components/RouteError';
import { AppShell } from '../layout/AppShell';
import { PhoneProvider } from '../telephony/PhoneProvider';
import { ProjectContext, type ProjectContextValue } from './context';

// ProjectLayout löst den Projektschlüssel aus der URL auf. Die URL ist die Quelle
// des aktiven Projekts (Deep Links, mehrere Tabs); der Server merkt sich den letzten Wechsel.
export function ProjectLayout() {
  const me = useMe();
  const { projectKey } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const project = me.projects.find((p) => p.key === projectKey);

  const switchProject = useCallback(
    (key: string) => {
      const target = me.projects.find((p) => p.key === key);
      if (!target || target.key === projectKey) return;
      // Der Bereich (z. B. Leads, Geo-Klärung) bleibt erhalten; Detailseiten und Filter gehören zum alten Projekt.
      const section: string[] = [];
      for (const segment of pathname.split('/').slice(3)) {
        if (!segment || /^[0-9a-f-]{36}$/i.test(segment)) break;
        section.push(segment);
      }
      navigate(['/p', target.key, ...section].join('/'));
      queryClient.setQueryData<UserContext | null>(meQueryKey, (prev) => (prev ? { ...prev, active_project_id: target.id } : prev));
      // Die gespeicherte Präferenz ist Komfort; die Navigation funktioniert auch ohne sie.
      api.setActiveProject(target.id).catch(() => undefined);
    },
    [me.projects, navigate, pathname, projectKey, queryClient],
  );

  const value = useMemo<ProjectContextValue | null>(() => {
    if (!project) return null;
    const can = (permission: Permission) =>
      project.permissions.includes(permission) || me.global_permissions.includes(permission);
    const switchable = me.projects.filter((p) => p.id === project.id || p.permissions.includes('project.switch'));
    return { project, switchable, can, switchProject };
  }, [project, me.projects, me.global_permissions, switchProject]);

  if (!value) {
    return <NotFoundPage message="Projekt nicht gefunden oder kein Zugriff." />;
  }
  return (
    <ProjectContext value={value}>
      <PhoneProvider>
        <AppShell />
      </PhoneProvider>
    </ProjectContext>
  );
}

// ProjectRedirect leitet auf das aktive (bzw. erste) Projekt weiter; `section` hängt einen
// Bereich an (z. B. /profil → /p/<projekt>/profil, Rücksprung aus dem Outlook-Login).
export function ProjectRedirect({ section }: { section?: string }) {
  const me = useMe();
  const { search } = useLocation();
  const active = me.projects.find((p) => p.id === me.active_project_id) ?? me.projects[0];
  if (!active) {
    return (
      <div className="center-page">
        <div className="card narrow">
          <h1>Kein Projekt zugeordnet</h1>
          <p className="muted">Ihnen ist noch kein Projekt zugeordnet. Bitte wenden Sie sich an Ihre Projektleitung.</p>
        </div>
      </div>
    );
  }
  return <Navigate to={`/p/${active.key}${section ? `/${section}` : ''}${search}`} replace />;
}
